(() => {
  const DEBOUNCE_MS = 250;
  const instances = new WeakMap();

  const normalize = (value) => {
    if (typeof value !== 'string') {
      return '';
    }

    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
  };

  const isSafeUrl = (url) => {
    if (typeof url !== 'string' || url.trim() === '') {
      return false;
    }

    if (url.startsWith('/') && url.startsWith('//') === false) {
      return true;
    }

    try {
      const parsed = new URL(url, window.location.origin);

      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
      return false;
    }
  };

  const rankText = (text, query) => {
    const value = normalize(text);
    const needle = normalize(query);

    if (needle === '' || value.includes(needle) === false) {
      return 3;
    }

    if (value === needle) {
      return 0;
    }

    if (value.startsWith(needle)) {
      return 1;
    }

    return 2;
  };

  const findMatch = (text, query) => {
    const needle = normalize(query);

    if (needle === '') {
      return null;
    }

    const chars = Array.from(text);
    const sourceIndexes = [];
    let folded = '';

    for (let index = 0; index < chars.length; index += 1) {
      const foldedChar = normalize(chars[index]);

      for (let piece = 0; piece < foldedChar.length; piece += 1) {
        sourceIndexes.push(index);
      }

      folded += foldedChar;
    }

    const matchIndex = folded.indexOf(needle);

    if (matchIndex === -1) {
      return null;
    }

    const start = sourceIndexes[matchIndex];
    const end = sourceIndexes[matchIndex + needle.length - 1] + 1;

    return { start, end };
  };

  const indexRequests = new Map();

  const fetchHelpJson = (fileName) => {
    const cached = indexRequests.get(fileName);

    if (cached != null) {
      return cached;
    }

    const request = fetch(`/cdn/shop/files/${fileName}?v=${Date.now()}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Help search: failed to fetch ${fileName}`);
        }

        return response.json();
      })
      .catch((error) => {
        indexRequests.delete(fileName);
        throw error;
      });

    indexRequests.set(fileName, request);

    return request;
  };

  const readJson = (root, selector) => {
    const node = root.querySelector(selector);

    if (node == null || node.textContent.trim() === '') {
      return null;
    }

    return JSON.parse(node.textContent);
  };

  class HelpSearch {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-help-search-form]');
      this.input = root.querySelector('[data-help-search-input]');
      this.predictive = root.querySelector('[data-help-search-predictive]');
      this.popup = root.querySelector('[data-help-search-popup]');
      this.tabs = root.querySelector('[data-help-search-tabs]');
      this.activeType = 'category';
      this.activeIndex = -1;
      this.options = [];
      this.timer = 0;
      this.fileName = root.dataset.helpFile || 'help.json';
      this.localeRoot = root.dataset.localeRoot || '';
      this.destroyed = false;
      this.index = { categories: [], articles: [] };
      this.labels = {};
      this.predictiveLimit = 5;
      this.minLength = 2;
      this.resultsUrl = this.form == null ? window.location.pathname : this.form.action;
      this.indexReady = Promise.resolve();

      if (this.form == null || this.input == null || this.predictive == null || this.popup == null || this.tabs == null) {
        return;
      }

      this.#readConfig();
      this.indexReady = this.#loadIndex();
      this.onInput = () => this.#onInput();
      this.onKeyDown = (event) => this.#onKeyDown(event);
      this.onPointerDown = (event) => this.#onPointerDown(event);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('pointerdown', this.onPointerDown);
    }

    destroy() {
      this.destroyed = true;
      window.clearTimeout(this.timer);

      if (this.input != null && this.onInput != null) {
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('keydown', this.onKeyDown);
      }

      if (this.onPointerDown != null) {
        document.removeEventListener('pointerdown', this.onPointerDown);
      }

      this.root.removeAttribute('data-help-search-ready');
    }

    #readConfig() {
      try {
        const config = readJson(this.root, '[data-help-search-config]');

        if (config != null) {
          this.predictiveLimit = Number(config.predictiveLimit) || 5;
          this.minLength = Number(config.minLength) || 2;
          this.resultsUrl = typeof config.resultsUrl === 'string' ? config.resultsUrl : this.resultsUrl;
          this.labels = config.labels || {};
        }
      } catch {
        this.labels = {};
      }
    }

    #localizeUrl(url) {
      if (typeof url !== 'string' || url.startsWith('/') === false || url.startsWith('//')) {
        return url;
      }

      const root = this.localeRoot;

      if (root === '' || url === root || url.startsWith(`${root}/`)) {
        return url;
      }

      return `${root}${url}`;
    }

    #assignIndex(index) {
      const categories = Array.isArray(index?.categories) ? index.categories : [];
      const articles = Array.isArray(index?.articles) ? index.articles : [];

      this.index = {
        categories: categories.map((item) => ({
          title: item?.title,
          url: this.#localizeUrl(item?.url),
        })),
        articles: articles.map((item) => ({
          question: item?.question,
          url: this.#localizeUrl(item?.url),
        })),
      };
    }

    async #loadIndex() {
      try {
        this.#assignIndex(await fetchHelpJson(this.fileName));
      } catch {
        if (this.fileName === 'help.json') {
          this.#assignIndex(null);
          return;
        }

        try {
          this.#assignIndex(await fetchHelpJson('help.json'));
        } catch {
          this.#assignIndex(null);
        }
      }
    }

    #onInput() {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        const query = this.input.value.trim();

        this.indexReady.then(() => {
          if (this.destroyed || this.input.value.trim() !== query) {
            return;
          }

          this.#renderPredictive(query);
        });
      }, DEBOUNCE_MS);
    }

    #onKeyDown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.#moveActive(1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.#moveActive(-1);
        return;
      }

      if (event.key === 'Escape') {
        if (this.popup.hidden === false) {
          event.preventDefault();
          this.#closePredictive();
        }

        return;
      }

      if (event.key === 'Enter' && this.activeIndex >= 0) {
        const option = this.options[this.activeIndex];

        if (option != null && isSafeUrl(option.href)) {
          event.preventDefault();
          window.location.href = option.href;
        }
      }
    }

    #onPointerDown(event) {
      if (this.root.contains(event.target) === false) {
        this.#closePredictive();
      }
    }

    #moveActive(direction) {
      if (this.options.length === 0) {
        return;
      }

      const nextIndex = this.activeIndex + direction;

      if (nextIndex >= this.options.length) {
        this.activeIndex = 0;
      } else if (nextIndex < 0) {
        this.activeIndex = this.options.length - 1;
      } else {
        this.activeIndex = nextIndex;
      }

      this.#syncActiveOption();
    }

    #syncActiveOption() {
      for (let index = 0; index < this.options.length; index += 1) {
        const option = this.options[index];
        const selected = index === this.activeIndex;

        option.classList.toggle('help-search__result--selected', selected);
        option.setAttribute('aria-selected', selected ? 'true' : 'false');

        if (selected) {
          this.input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      }

      if (this.activeIndex === -1) {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    #closePredictive() {
      this.popup.hidden = true;
      this.predictive.replaceChildren();
      this.tabs.hidden = true;
      this.tabs.replaceChildren();
      this.tabs.className = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.options = [];
      this.activeIndex = -1;
    }

    #openPredictive() {
      this.popup.hidden = false;
      this.tabs.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    #icon(name) {
      const template = this.root.querySelector(`[data-help-search-icon="${name}"]`);

      if (template == null) {
        return document.createDocumentFragment();
      }

      return template.content.cloneNode(true);
    }

    #renderTabs(groups) {
      this.tabs.replaceChildren();
      this.tabs.className = 'tabs-nav tabs-nav--center tabs-nav--no-border tabs-nav--edge2edge';
      this.tabs.hidden = false;

      const scroller = document.createElement('div');
      const scrollerInner = document.createElement('div');
      const list = document.createElement('div');
      const position = document.createElement('span');

      scroller.className = 'tabs-nav__scroller hide-scrollbar';
      scrollerInner.className = 'tabs-nav__scroller-inner';
      list.className = 'tabs-nav__item-list';
      position.className = 'tabs-nav__position';

      for (const group of groups) {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'tabs-nav__item heading heading--small';
        button.setAttribute('aria-expanded', group.type === this.activeType ? 'true' : 'false');
        button.setAttribute('aria-controls', group.panelId);
        button.textContent = `${group.label} (${group.items.length})`;
        button.addEventListener('click', () => {
          if (group.type === this.activeType) {
            return;
          }

          this.activeType = group.type;
          this.#paintResults();
        });
        list.append(button);
      }

      scrollerInner.append(list, position);
      scroller.append(scrollerInner);
      this.tabs.append(scroller);
      requestAnimationFrame(() => {
        this.#placeTabIndicator(this.tabs);
      });
    }

    #placeTabIndicator(nav) {
      const selected = nav.querySelector('[aria-expanded="true"]');
      const position = nav.querySelector('.tabs-nav__position');
      const parent = position?.parentElement;

      if (selected == null || position == null || parent == null || parent.clientWidth === 0) {
        return;
      }

      const scale = selected.clientWidth / parent.clientWidth;
      const translate = selected.offsetLeft / parent.clientWidth / scale;

      position.style.setProperty('--scale', String(scale));
      position.style.setProperty('--translate', `${translate * 100}%`);
      position.classList.add('is-initialized');
    }

    #renderResultList(items, field, query, idPrefix) {
      const list = document.createElement('ul');

      list.className = 'predictive-search__linklist predictive-search__linklist--narrow predictive-search__linklist--bordered list--unstyled';
      list.setAttribute('role', 'list');

      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        const itemElement = document.createElement('li');
        const link = this.#createOption(`${idPrefix}-${index}`, item.url, item[field], query, 'predictive-search__linklist-link');

        itemElement.className = 'predictive-search__linklist-item';
        link.append(this.#icon('link'));
        itemElement.append(link);
        list.append(itemElement);
        this.options.push(link);
      }

      return list;
    }

    #search(items, field, query) {
      return items
        .map((item, index) => ({
          item,
          index,
          rank: rankText(item?.[field], query),
        }))
        .filter((entry) => entry.rank < 3 && isSafeUrl(entry.item?.url))
        .sort((left, right) => left.rank - right.rank || left.index - right.index)
        .map((entry) => entry.item);
    }

    #resultsUrlFor(query) {
      const url = new URL(this.resultsUrl, window.location.origin);

      url.searchParams.set('q', query);

      return `${url.pathname}${url.search}`;
    }

    #highlight(element, text, query) {
      const match = findMatch(text, query);

      element.replaceChildren();

      if (match == null) {
        element.textContent = text;
        return;
      }

      const mark = document.createElement('mark');

      mark.className = 'help-search__mark';
      mark.textContent = text.slice(match.start, match.end);
      element.append(
        document.createTextNode(text.slice(0, match.start)),
        mark,
        document.createTextNode(text.slice(match.end)),
      );
    }

    #createOption(id, url, text, query, className) {
      const link = document.createElement('a');
      const label = document.createElement('span');

      link.className = className;
      link.href = url;
      link.id = id;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      label.className = 'help-search__result-label';

      if (query == null) {
        label.textContent = text;
      } else {
        this.#highlight(label, text, query);
      }

      link.append(label);

      return link;
    }

    #paintResults() {
      const groups = [
        {
          type: 'category',
          label: this.labels.categories,
          field: 'title',
          items: this.matchedCategories,
          panelId: `HelpSearchPanel-${this.input.id}-category`,
        },
        {
          type: 'article',
          label: this.labels.pages || this.labels.articles,
          field: 'question',
          items: this.matchedArticles,
          panelId: `HelpSearchPanel-${this.input.id}-article`,
        },
      ].filter((group) => group.items.length > 0);

      if (groups.some((group) => group.type === this.activeType) === false) {
        this.activeType = groups[0].type;
      }

      const activeGroup = groups.find((group) => group.type === this.activeType);
      const visibleItems = activeGroup.items.slice(0, this.predictiveLimit);
      const panel = document.createElement('div');
      const viewAll = this.#createOption(
        `${this.input.id}-all`,
        this.#resultsUrlFor(this.query),
        this.labels.viewAll,
        null,
        'predictive-search__linklist-link button button--primary button--full help-search__view-all',
      );

      this.#renderTabs(groups);
      panel.id = activeGroup.panelId;
      panel.append(this.#renderResultList(visibleItems, activeGroup.field, this.query, `${this.input.id}-${activeGroup.type}`));
      this.options.push(viewAll);
      this.predictive.replaceChildren(panel, viewAll);
    }

    #renderPredictive(query) {
      this.activeIndex = -1;
      this.options = [];
      this.query = query;
      this.predictive.replaceChildren();

      if (query.length < this.minLength) {
        this.#closePredictive();
        return;
      }

      this.matchedCategories = this.#search(this.index.categories, 'title', query);
      this.matchedArticles = this.#search(this.index.articles, 'question', query);

      if (this.matchedCategories.length === 0 && this.matchedArticles.length === 0) {
        const empty = document.createElement('p');
        const searchLink = this.#createOption(
          `${this.input.id}-empty`,
          this.#resultsUrlFor(query),
          `${this.labels.searchFor} "${query}"`,
          null,
          'predictive-search__linklist-link button button--primary button--full help-search__view-all',
        );

        this.tabs.hidden = true;
        this.tabs.replaceChildren();
        this.tabs.className = '';
        empty.className = 'help-search__empty';
        empty.textContent = this.labels.predictiveEmpty;
        this.predictive.append(empty, searchLink);
        this.options.push(searchLink);
        this.popup.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        return;
      }

      this.#paintResults();
      this.#openPredictive();
    }
  }

  const initHelpSearch = (scope) => {
    const roots = scope.querySelectorAll('[data-help-search]');

    for (const root of roots) {
      if (root.getAttribute('data-help-search-ready') === 'true') {
        continue;
      }

      root.setAttribute('data-help-search-ready', 'true');
      instances.set(root, new HelpSearch(root));
    }
  };

  const destroyHelpSearch = (scope) => {
    const roots = scope.matches?.('[data-help-search]')
      ? [scope]
      : scope.querySelectorAll('[data-help-search]');

    for (const root of roots) {
      const instance = instances.get(root);

      if (instance != null) {
        instance.destroy();
        instances.delete(root);
      }
    }
  };

  if (window.__helpSearchBound !== true) {
    window.__helpSearchBound = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initHelpSearch(document);
      });
    } else {
      initHelpSearch(document);
    }

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target != null) {
        initHelpSearch(event.target);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target != null) {
        destroyHelpSearch(event.target);
      }
    });
  }
})();
